/**
 * An audience as a card, for the list page's reflowing card grid — the audiences twin of
 * `dashboard/components/DashboardCard.tsx`, built on the shared {@link ListCard} shell so the two
 * lists share one layout family and reflow 1→2→3 columns instead of clipping columns off a
 * fixed-width table.
 *
 * Identity: warehouse-type logo via {@link AudienceWarehouseMark} in `titleIcon` (ACT2-640), then the
 * title and status badge. The warehouse NAME stays on hover / search only — not visible text — so the
 * card and table stay two renderings of one list.
 *
 * Meta: Size ({@link CountBadge}) plus an activation count chip when connected (hide at zero, same
 * wording as the detail page's AudienceActivationsLink). The conditions count stays on the detail
 * page. The list is read-only for counts (no Calculate here). The warehouse mark and tooltip-bearing
 * indicators sit above the stretched navigation link so they stay hoverable; the mark links to the
 * same audience, while the count text stays under the stretched link. The mark is decorative. The
 * stretched link's `ariaLabel` names the warehouse via {@link audienceRowLabel}.
 */
import { Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';

import { NeedsRepairBadge } from '@/_shared/components/AccountRepair/NeedsRepairBadge';
import { KebabMenu } from '@/_shared/components/list/KebabMenu';
import { ListCard } from '@/_shared/components/list/ListCard';
import { timeAgo } from '@/_shared/lib/format';

import { activationCountLabel, audienceRowLabel, isCountStale } from '../lib/audienceList';
import type { AudienceListItem } from '../types';
import { AudienceStatusBadge } from './AudienceStatusBadge';
import { AudienceWarehouseMark } from './AudienceWarehouseMark';
import { CountBadge } from './CountBadge';
import { StaleIndicator } from './StaleIndicator';

interface AudienceCardProps {
  audience: AudienceListItem;
  /** Destination href for the whole card (built with `wp()`). */
  to: string;
  /** When provided, renders a Delete action (the caller gates whether this user may delete this audience). */
  onDelete?: (id: string) => void;
  /**
   * Why `Delete` is unavailable, or `undefined` when it is not: a disabled or gone sibling turns audience actions
   * off, with the banner's text as the reason (ACT2-1124 LLD §5.9). Required, as in `buildAudienceColumns`, so a
   * caller cannot pass `onDelete` and forget the gate.
   */
  deleteDisabledReason: string | undefined;
}

export function AudienceCard({ audience, to, onDelete, deleteDisabledReason }: AudienceCardProps): React.ReactElement {
  return (
    <ListCard
      to={to}
      title={audience.title}
      ariaLabel={`Open ${audienceRowLabel(audience)}`}
      titleIcon={
        // Keep the mark hoverable and clickable above the overlay. The main card link owns keyboard
        // navigation and the accessible name, so this duplicate pointer target adds no tab stop.
        <Link to={to} tabIndex={-1} aria-hidden className="relative z-10 inline-flex">
          <AudienceWarehouseMark type={audience.warehouseType} warehouseName={audience.warehouseLabel} />
        </Link>
      }
      badges={
        <>
          <AudienceStatusBadge status={audience.status} />
          <NeedsRepairBadge listRow state={audience.repairState ?? 'ok'} className="shrink-0" />
        </>
      }
      actions={
        onDelete ? (
          <KebabMenu
            label={`Actions for ${audience.title}`}
            actions={[
              {
                label: 'Delete',
                icon: Trash2,
                destructive: true,
                disabled: deleteDisabledReason !== undefined,
                disabledReason: deleteDisabledReason ?? '',
                onSelect: () => onDelete(audience.id),
              },
            ]}
          />
        ) : undefined
      }
      // Size + its stale/error markers lead the meta line (count-adjacent, like the detail page — not up by
      // the title). Only the stale/error TOOLTIP triggers are lifted above the card's stretched link
      // (`relative z-10`, per element) so they stay hoverable — the count text itself stays under the link so
      // clicking it still navigates. Lifting the whole cluster instead made the count a dead click zone.
      // Activation count (ACT2-640) follows Size when non-zero; hide at zero so an unconnected audience
      // stays quiet — same quiet-zero rule as AudienceActivationsLink on the detail page.
      meta={
        <span className="inline-flex items-center gap-1.5">
          <CountBadge state={audience.countState} />
          {isCountStale(audience) ? <StaleIndicator className="relative z-10" /> : null}
          {audience.activationCount > 0 ? (
            <span className="text-xs font-normal text-muted-foreground">
              {activationCountLabel(audience.activationCount)}
            </span>
          ) : null}
        </span>
      }
      footerRight={`Updated ${timeAgo(audience.updatedAt)}`}
    />
  );
}
